import type { InternalAxiosRequestConfig } from "axios";

export type RequestLogProperties = Record<string, string | boolean | number>;

function mode(value: unknown): string | undefined {
	const text = String(value ?? "").toLowerCase();
	return text === "staff"
		? "Staff"
		: text === "student"
			? "Student"
			: text === "parent"
				? "Parent"
				: undefined;
}

/** Selected identity claims only; never keep the bearer token or other claims. */
export function browserRequestContext(
	config: InternalAxiosRequestConfig,
	url: URL,
	apiPrefix: string,
): RequestLogProperties {
	const properties: RequestLogProperties = {
		RequestContextSource: "Browser",
		RequestMethod: config.method?.toUpperCase() ?? "GET",
		RequestPath: `/api/${url.pathname.slice(apiPrefix.length)}`,
        AuthenticationStatus: "Unverified",
        ImpersonationStatus: "None",
	};
	const host = mode(config.headers.get("x-host-mode"));
	const view = mode(config.headers.get("x-view-mode"));
	if (host) properties.HostMode = host;
	if (view) properties.ViewMode = view;
	try {
		const authorization = String(config.headers.get("Authorization") ?? "");
		if (!authorization.startsWith("Bearer ")) return properties;
		const encoded = authorization.slice(7).split(".")[1];
		const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
		const bytes = Uint8Array.from(atob(normalized), (c) => c.charCodeAt(0));
		const claims = JSON.parse(new TextDecoder().decode(bytes));
        // Decoding claims does not establish that the server accepted the token.
		for (const [key, value] of Object.entries({
			Requester:
				claims.preferred_username ?? claims.upn ?? claims.email ?? claims.name,
			RequesterId: claims.oid ?? claims.sub,
			RequesterTenantId: claims.tid,
		}))
			if (typeof value === "string" && value) properties[key] = value;
	} catch {
		/* A missing or non-JWT credential must not break tracing. */
	}
	// Use the actual outgoing override, not mutable global UI state. Server
	// response context will confirm or reject it before the span is exported.
	const params = new URLSearchParams(url.search);
	const override = params.get("usernameOverride")?.trim();
	if (host === "Staff" && override) {
		const student = /^student:(\d+)$/i.exec(override);
		const parent = /^parent:([^\s@]+@[^\s@]+)$/i.exec(override);
		if (student || parent || !override.includes(":")) {
            properties.ImpersonationStatus = "Requested";
			properties.ImpersonatedUser = student
				? student[1]
				: parent
					? parent[1].toLowerCase()
					: override;
			properties.ImpersonatedHostMode = student
				? "Student"
				: parent
					? "Parent"
					: "Staff";
		}
	}
	return properties;
}

const sharedKeys = new Set([
    "AuthenticationStatus",
    "ImpersonationStatus",
	"RequestContextSource",
	"Requester",
	"RequesterId",
	"RequesterTenantId",
	"RequesterUsername",
	"UserAuthenticated",
	"IsImpersonating",
	"ImpersonatedUser",
	"ImpersonatedHostMode",
	"HostMode",
	"ViewMode",
	"CorrelationId",
	"RequestMethod",
	"RequestPath",
]);

export function serverRequestContext(
	header: unknown,
): RequestLogProperties | undefined {
	if (typeof header !== "string") return;
	try {
		const parsed = JSON.parse(header);
		if (parsed?.RequestContextSource !== "Server") return;
		return Object.fromEntries(
			Object.entries(parsed).filter(
				([key, value]) =>
					sharedKeys.has(key) &&
					(typeof value === "string" ||
						typeof value === "boolean" ||
						typeof value === "number"),
			),
		) as RequestLogProperties;
	} catch {
		return;
	}
}
